import {getAiToolkit} from '@tiptap-pro/ai-toolkit';

// Get error result
const getErrorObject = (toolCallId, toolName, parentToolUseId, output) => ({
  tool_call_id: toolCallId,
  tool_name: toolName,
  output,
  success: false,
  docChanged: false,
  parent_tool_use_id: parentToolUseId || null,
});

/**
 * Execute a single tool call on the appropriate editor.
 * Routes the tool call to a specific editor based on sectionId in the input.
 * @param {Object} editors - Map of sectionId -> editor instance
 * @param {Object} toolCall - Single tool call from the backend
 * @param {Object} options - Execution options
 * @param {string} options.mode - Review mode ('review' | 'direct')
 * @param {Object} options.metadata - Metadata to attach to suggestions
 * @returns {Object} - {tool_call_id, tool_name, output, success, docChanged, parent_tool_use_id}
 */
export function executeToolOnEditor(editors, toolCall, options = {}) {
  const {
    mode = 'preview',
    metadata = {type: 'penny-chat'},
    tiptapEditHooks,
  } = options;

  const {
    tool_name: toolName,
    tool_call_id: toolCallId,
    input,
    parent_tool_use_id: parentToolUseId,
  } = toolCall;

  // Check if we have any valid editors
  const hasValidEditors = Object.values(editors || {}).some(
    (editor) => editor && !editor.isDestroyed
  );

  if (!hasValidEditors) {
    console.warn('[TiptapToolExecutor] No valid editors available');
    return getErrorObject(
      toolCallId,
      toolName,
      parentToolUseId,
      'Error: No editor available'
    );
  }

  // Extract sectionId for routing, pass rest to toolkit
  const {sectionId, ...toolkitInput} = input || {};

  if (!sectionId) {
    console.warn(
      `[TiptapToolExecutor] No sectionId provided for tool: ${toolName}`
    );
    return getErrorObject(
      toolCallId,
      toolName,
      parentToolUseId,
      'Error: sectionId is required'
    );
  }

  const editor = editors[sectionId];
  if (!editor || editor.isDestroyed) {
    console.warn(
      `[TiptapToolExecutor] Editor not found for section: ${sectionId}`
    );
    return getErrorObject(
      toolCallId,
      toolName,
      parentToolUseId,
      `Error: Editor not found for section: ${sectionId}`
    );
  }

  const toolkit = getAiToolkit(editor);

  try {
    const result = toolkit.executeTool({
      toolName,
      input: toolkitInput,
      reviewOptions: {mode, metadata},
      ...(tiptapEditHooks && {tiptapEditHooks}),
    });

    const outputStr = JSON.stringify(result.output ?? '');
    return {
      tool_call_id: toolCallId,
      tool_name: toolName,
      output: outputStr,
      success: true,
      docChanged: result.docChanged || false,
      parent_tool_use_id: parentToolUseId || null,
    };
  } catch (error) {
    console.error(
      `[TiptapToolExecutor] Error executing tool ${toolName}:`,
      error
    );
    return getErrorObject(
      toolCallId,
      toolName,
      parentToolUseId,
      `Error: ${error.message || 'Unknown error'}`
    );
  }
}

/**
 * Get section contents via tiptapRead tool execution for front-loading document context.
 *
 * Uses executeTool (not the tiptapRead method) so that:
 *   1. _hash attributes are embedded in the returned HTML
 *   2. The toolkit's internal change-tracking state is initialized,
 *      which tiptapEdit requires ("Node content changed since last read" otherwise)
 *
 * @param {Object} editors - Map of sectionId -> editor instances
 * @returns {Object|null} - Map of sectionId -> content HTML string, or null if empty
 */
export function getSectionContents(editors) {
  if (!editors) return null;

  const contents = {};
  for (const [sectionId, editor] of Object.entries(editors)) {
    if (editor && !editor.isDestroyed) {
      try {
        const toolkit = getAiToolkit(editor);
        const chunks = [];
        let from = 0;
        let totalNodeCount = Infinity;

        while (from < totalNodeCount) {
          const result = toolkit.executeTool({
            toolName: 'tiptapRead',
            input: {from},
          });

          const output = result.output;
          if (!output || !output.content) break;

          chunks.push(output.content);
          totalNodeCount = output.totalNodeCount ?? totalNodeCount;

          const nodeRange = output.nodeRange;
          if (!nodeRange || nodeRange[1] >= totalNodeCount - 1) break;
          from = nodeRange[1] + 1;
        }

        const fullContent = chunks.join('');

        if (fullContent) {
          contents[sectionId] = fullContent;
        }
      } catch (error) {
        console.warn(
          `[TiptapToolExecutor] Failed to get content for section ${sectionId}:`,
          error
        );
      }
    }
  }
  return Object.keys(contents).length > 0 ? contents : null;
}

export default {
  executeToolOnEditor,
  getSectionContents,
};
